import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  AlertColor,
  Box as MuiBox,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import Notification from 'components/Notification';
import { ToolTip } from 'components/Tooltip/ToolTip';
import { useCallback, useEffect, useRef, useState } from 'react';
import Debounce from 'utils/validators/debounce';
import strings from '../../../../../common/Translation/Translate';

const TextBoxField = ({ ValueFormatterParams, handleTextChange, field }: any) => {
  const [val, setVal] = useState<any>(
    field === 'price' ? ValueFormatterParams.row.price : ValueFormatterParams.row.quantity,
  );
  const handleTextChangeRef = useRef(handleTextChange);
  handleTextChangeRef.current = handleTextChange;

  const optimizedFn = useCallback(
    Debounce((val: string) => handleTextChangeRef.current(val, ValueFormatterParams.row, field), 1500),
    [ValueFormatterParams.row, field],
  ) as any;

  return (
    <TextField
      sx={{
        width: '100%',
      }}
      size="small"
      required
      id="outlined-required"
      disabled={!ValueFormatterParams.row.selected}
      value={val}
      type="number"
      inputProps={{
        min: field === 'quantity' && ValueFormatterParams.row.addOnType === 'PER_QUANTITY' ? 1 : 0,
      }}
      onKeyDown={e => {
        if (field === 'quantity' && ValueFormatterParams.row.addOnType === 'PER_QUANTITY') {
          if (e.key === '0' && val === '') {
            e.preventDefault();
          }
        }
      }}
      onChange={e => {
        const v = e.target.value;
        if (!Number.isNaN(Number(v))) {
          if (field === 'quantity' && ValueFormatterParams.row.addOnType === 'PER_QUANTITY') {
            const numValue = parseInt(v);
            if (v === '' || numValue < 1) {
              return;
            }
          }
          setVal(v);
          optimizedFn(v);
        }
      }}
    />
  );
};

const CustomPagination = ({ page, pageSize, totalRows, onPageChange, onPageSizeChange }: any) => {
  const theme = useTheme();
  const totalPages = Math.ceil(totalRows / pageSize);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <MuiBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        minHeight: '56px',
      }}>
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Rows per page:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={pageSize}
            onChange={e => onPageSizeChange(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid',
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={-1}>All</MenuItem>
          </Select>
        </FormControl>
      </MuiBox>

      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {`${page * pageSize - pageSize + 1}-${Math.min(page * pageSize, totalRows)} of ${totalRows}`}
        </Typography>

        <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            sx={{
              minWidth: 32,
              height: 32,
              borderRadius: '50%',
              padding: 0,
            }}>
            ←
          </Button>

          {getPageNumbers().map((pageNum, index) => (
            <Button
              key={index}
              size="small"
              onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
              disabled={pageNum === '...'}
              sx={{
                minWidth: 32,
                height: 32,
                borderRadius: '50%',
                padding: 0,
                backgroundColor: pageNum === page ? 'primary.main' : 'transparent',
                color: pageNum === page ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: pageNum === page ? 'primary.dark' : 'action.hover',
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  color: 'text.secondary',
                },
              }}>
              {pageNum}
            </Button>
          ))}

          <Button
            size="small"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            sx={{
              minWidth: 32,
              height: 32,
              borderRadius: '50%',
              padding: 0,
            }}>
            →
          </Button>
        </MuiBox>
      </MuiBox>
    </MuiBox>
  );
};

const BookingRoomsTable = ({ item, handleCheck, handleTextChange }: any) => {
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const totalPages = Math.ceil(item.length / pageSize);
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [item.length, pageSize, page]);
  const columns = [
    {
      field: 'name',
      headerName: strings.bookingDetails_text_addOn_name,
      sortable: false,
      flex: 2,
      minWidth: 200,
      hideable: false,
      renderCell: (ValueFormatterParams: any) => {
        return (
          <Box sx={{ whiteSpace: 'nowrap', overflowX: 'hidden', textOverflow: 'ellipsis' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ValueFormatterParams.row.selected}
                    onChange={e => handleCheck(e, ValueFormatterParams.row)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {ValueFormatterParams.row.description && (
                      <ToolTip
                        title={
                          <Typography dangerouslySetInnerHTML={{ __html: ValueFormatterParams.row.description }} />
                        }>
                        <InfoOutlined fontSize="small" />
                      </ToolTip>
                    )}
                    <ToolTip title={ValueFormatterParams.row.name}>
                      <Typography>{ValueFormatterParams.row.name}</Typography>
                    </ToolTip>
                  </Box>
                }
              />
            </FormGroup>
          </Box>
        );
      },
    },
    {
      field: 'addOnType',
      headerName: strings.bookingDetails_text_addOn_type,
      flex: 1,
      minWidth: 150,
      hideable: false,
    },
    {
      field: 'price',
      headerName: strings.bookingDetails_text_addOn_price,
      flex: 1,
      minWidth: 100,
      hideable: false,
      renderCell: (ValueFormatterParams: any) => {
        return (
          <TextBoxField
            ValueFormatterParams={ValueFormatterParams}
            handleTextChange={handleTextChange}
            field={'price'}
          />
        );
      },
    },
    {
      field: 'quantity',
      headerName: strings.bookingDetails_text_addOn_quantity,
      sortable: false,
      flex: 1,
      minWidth: 100,
      hideable: false,
      renderCell: (ValueFormatterParams: any) => {
        return ValueFormatterParams.row.addOnType === 'PER_QUANTITY' ? (
          <TextBoxField
            ValueFormatterParams={ValueFormatterParams}
            handleTextChange={handleTextChange}
            field={'quantity'}
          />
        ) : (
          <span></span>
        );
      },
    },
  ];
  const value =
    item?.length > 0 ? (
      <div style={{ width: '100%' }}>
        <div style={{ minHeight: '400px', width: '100%', paddingLeft: '17px' }}>
          <DataGrid
            key={`datagrid-${pageSize}`}
            sx={{
              height: pageSize === -1 ? 'auto' : 400,
              overflowY: 'auto',
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
              },
            }}
            disableColumnSelector
            rowHeight={50}
            rows={item}
            autoHeight={pageSize === -1}
            columns={columns}
            pageSize={pageSize === -1 ? item.length : pageSize}
            page={page - 1}
            onPageChange={newPage => setPage(newPage + 1)}
            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
            hideFooterPagination
            disableSelectionOnClick
          />
          {pageSize !== -1 && (
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={item.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      </div>
    ) : null;
  return value;
};

const BookedAddOns = ({ addOnsByCategory, handleCheck, handleTextChange }: any) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const categories = Object.keys(addOnsByCategory).filter(categoryKey => addOnsByCategory[categoryKey].length > 0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getSelectedCountForCategory = (category: string) => {
    return (addOnsByCategory[category] || []).filter((item: any) => item.selected).length;
  };

  const getTotalSelectedCount = () => {
    return categories.reduce((total, category) => total + getSelectedCountForCategory(category), 0);
  };

  if (categories.length === 0) {
    return null;
  }

  return (
    <Box mb={2}>
      {getTotalSelectedCount() > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
            {getTotalSelectedCount()} addon{getTotalSelectedCount() !== 1 ? 's' : ''} selected across all categories
          </Typography>
        </Paper>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
              position: 'relative',
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
          }}>
          {categories.map((category, index) => {
            const selectedCount = getSelectedCountForCategory(category);
            return (
              <Tab
                key={category}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                      {category}
                    </Typography>
                    {selectedCount > 0 && (
                      <Chip
                        label={selectedCount}
                        size="small"
                        color="primary"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                }
                sx={{
                  '&.Mui-selected': {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      backgroundColor: 'primary.main',
                    },
                  },
                }}
              />
            );
          })}
        </Tabs>

        {categories.map((category, index) => (
          <Box
            key={category}
            role="tabpanel"
            hidden={activeTab !== index}
            id={`addon-tabpanel-${index}`}
            aria-labelledby={`addon-tab-${index}`}>
            {activeTab === index && (
              <Box sx={{ p: 2 }}>
                <BookingRoomsTable
                  item={addOnsByCategory[category]}
                  handleCheck={handleCheck}
                  handleTextChange={handleTextChange}
                />
              </Box>
            )}
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

type BookingAddonProps = {
  setBookedAddOns: any;
  addonsDropdown: any;
  bookedAddOns: any;
};

export const BookingAddon: React.FC<BookingAddonProps> = ({ setBookedAddOns, addonsDropdown, bookedAddOns }) => {
  const [addOnsByCategory, setAddOnsByCategory] = useState<any>({});
  const [filteredAddOnsByCategory, setFilteredAddOnsByCategory] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const hasInitialized = useRef(false);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    selectedOnly: false,
    perQuantity: false,
    perGuest: false,
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };
  useEffect(() => {
    if (!addonsDropdown || addonsDropdown.length === 0 || hasInitialized.current) return;

    const selectedAddons = addonsDropdown
      .filter((item: any) => item.selected)
      .map((item: any) => ({
        type: item.addOnType,
        addOnId: item.id,
        name: item.name,
        id: item.bookedAddOnId,
        maxCount: item.maxCount,
        quantity:
          item.quantity === null
            ? item.addOnType === 'PER_QUANTITY'
              ? 1
              : 0
            : item.addOnType === 'PER_QUANTITY' && item.quantity < 1
            ? 1
            : item.quantity,
        price: item.price,
      }));

    if (selectedAddons.length > 0) {
      setBookedAddOns((prev: any) => {
        const existingAddOnIds = prev.map((item: any) => item.addOnId);
        const newAddons = selectedAddons.filter((addon: any) => !existingAddOnIds.includes(addon.addOnId));
        return [...prev, ...newAddons];
      });
    }

    hasInitialized.current = true;
  }, [addonsDropdown, setBookedAddOns]);

  const filterAddons = useCallback((addOnsData: any, search: string, currentFilters: any) => {
    const filtered: any = {};

    Object.keys(addOnsData).forEach(category => {
      const filteredItems = addOnsData[category].filter((addon: any) => {
        const matchesSearch =
          !search.trim() ||
          addon.name.toLowerCase().includes(search.toLowerCase()) ||
          (addon.description && addon.description.toLowerCase().includes(search.toLowerCase()));

        const matchesSelected = !currentFilters.selectedOnly || addon.selected;

        let matchesType = true;
        if (currentFilters.perQuantity) {
          matchesType = addon.addOnType === 'PER_QUANTITY';
        } else if (currentFilters.perGuest) {
          matchesType = addon.addOnType === 'PER_GUEST';
        }

        return matchesSearch && matchesSelected && matchesType;
      });

      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    });

    return filtered;
  }, []);

  const getTotalFilteredCount = () => {
    return Object.values(filteredAddOnsByCategory).reduce((total: number, category: any) => total + category.length, 0);
  };

  useEffect(() => {
    if (!addonsDropdown || addonsDropdown.length === 0) return;

    const temp: any = {};
    addonsDropdown.forEach((addOn: any) => {
      if (!temp[addOn.addOnCategory]) {
        temp[addOn.addOnCategory] = [];
      }
      temp[addOn.addOnCategory].push(addOn);
    });

    setAddOnsByCategory(temp);
  }, [addonsDropdown]);

  useEffect(() => {
    setFilteredAddOnsByCategory(filterAddons(addOnsByCategory, searchTerm, filters));
  }, [searchTerm, filters, addOnsByCategory, filterAddons]);

  const handleCheck = (event: { target: { checked: boolean } }, rowData: any) => {
    const value = event.target.checked;
    let updatedItem;

    const temp = addOnsByCategory[rowData.addOnCategory].map((item: any) => {
      if (item.id === rowData.id) {
        updatedItem = { ...item, selected: value };
        return updatedItem;
      }
      return { ...item };
    });

    if (value) {
      const obj = {
        type: rowData.addOnType,
        addOnId: rowData.id,
        maxCount: rowData.maxCount,
        name: rowData.name,
        id: rowData.bookedAddOnId,
        quantity:
          rowData.quantity === null
            ? rowData.addOnType === 'PER_QUANTITY'
              ? 1
              : 0
            : rowData.addOnType === 'PER_QUANTITY' && rowData.quantity < 1
            ? 1
            : rowData.quantity,
        price: rowData.price,
      };
      setBookedAddOns((prev: any) => {
        const existingIndex = prev.findIndex((item: any) => item.addOnId === rowData.id);
        if (existingIndex === -1) {
          return [...prev, obj];
        }
        return prev;
      });

      setAddOnsByCategory((prev: any) => ({
        ...prev,
        [rowData.addOnCategory]: temp,
      }));
    } else {
      const index = bookedAddOns.findIndex((item: any) => item.addOnId === rowData.id);
      const filteredBookedAddOns = bookedAddOns.filter((item: any, idx: any) => idx !== index);
      setBookedAddOns(filteredBookedAddOns);

      setAddOnsByCategory((prev: any) => ({
        ...prev,
        [rowData.addOnCategory]: temp,
      }));
    }
  };

  const handleTextChange = useCallback(
    (value: any, rowData: any, field: string) => {
      let v = value;
      const { id, addOnCategory } = rowData;
      if (field === 'quantity' && rowData.addOnType === 'PER_QUANTITY' && (v === '' || parseInt(v) < 1)) {
        v = '1';
      }

      const index = bookedAddOns.findIndex((item: any) => item.addOnId === id);

      const temp = addOnsByCategory[addOnCategory].map((item: any) => {
        if (item.id === id) {
          return { ...item, [field]: v === '' ? 0 : parseInt(v) };
        }
        return { ...item };
      });

      setAddOnsByCategory((prev: any) => ({
        ...prev,
        [rowData.addOnCategory]: temp,
      }));

      if (index === -1) {
        return;
      }

      const updatedBookedAddOns = bookedAddOns.map((item: any, idx: any) => {
        if (idx === index) {
          return { ...item, [field]: v === '' ? 0 : parseInt(v) };
        }
        return item;
      });
      setBookedAddOns(updatedBookedAddOns);
    },
    [bookedAddOns, addOnsByCategory],
  );
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilters({
      selectedOnly: false,
      perQuantity: false,
      perGuest: false,
    });
  };

  const handleFilterChange = (filterType: 'selectedOnly' | 'perQuantity' | 'perGuest', value: boolean) => {
    setFilters(prev => {
      const newFilters = { ...prev };

      if (filterType === 'perQuantity' && value === true) {
        newFilters.perGuest = false;
      } else if (filterType === 'perGuest' && value === true) {
        newFilters.perQuantity = false;
      }

      newFilters[filterType] = value;
      return newFilters;
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm.trim()) count++;
    if (filters.selectedOnly) count++;
    if (filters.perQuantity) count++;
    if (filters.perGuest) count++;
    return count;
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {addonsDropdown ? (
        <>
          <MuiBox sx={{ mb: 3 }}>
            <MuiBox sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search addons by name, description, or price..."
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle advanced filters"
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        size="small"
                        sx={{ mr: 1 }}>
                        <FilterListIcon />
                        {getActiveFiltersCount() > 0 && (
                          <Chip
                            label={getActiveFiltersCount()}
                            size="small"
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              height: 16,
                              minWidth: 16,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </IconButton>
                      {searchTerm && (
                        <IconButton aria-label="clear search" onClick={clearSearch} size="small">
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </MuiBox>

            <Collapse in={showAdvancedFilters}>
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }}>
                <MuiBox
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Filters
                  </Typography>
                  <Button
                    size="small"
                    onClick={clearAllFilters}
                    startIcon={<ClearIcon />}
                    disabled={getActiveFiltersCount() === 0}>
                    Clear All
                  </Button>
                </MuiBox>

                <MuiBox sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="Selected Only"
                    onClick={() => handleFilterChange('selectedOnly', !filters.selectedOnly)}
                    color={filters.selectedOnly ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="Per Quantity"
                    onClick={() => handleFilterChange('perQuantity', !filters.perQuantity)}
                    color={filters.perQuantity ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="Per Guest"
                    onClick={() => handleFilterChange('perGuest', !filters.perGuest)}
                    color={filters.perGuest ? 'primary' : 'default'}
                    size="small"
                  />
                </MuiBox>
              </Paper>
            </Collapse>

            {(searchTerm || getActiveFiltersCount() > 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {getTotalFilteredCount()} addon{getTotalFilteredCount() !== 1 ? 's' : ''} found across{' '}
                {Object.keys(filteredAddOnsByCategory).length} categor
                {Object.keys(filteredAddOnsByCategory).length !== 1 ? 'ies' : 'y'}
                {getActiveFiltersCount() > 0 &&
                  ` (${getActiveFiltersCount()} filter${getActiveFiltersCount() !== 1 ? 's' : ''} applied)`}
              </Typography>
            )}
          </MuiBox>

          {getTotalFilteredCount() > 0 ? (
            <BookedAddOns
              addOnsByCategory={filteredAddOnsByCategory}
              handleCheck={handleCheck}
              handleTextChange={handleTextChange}
            />
          ) : searchTerm ? (
            <MuiBox
              sx={{
                textAlign: 'center',
                py: 4,
                px: 2,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No addons found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or{' '}
                <Typography
                  component="span"
                  color="primary"
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={clearSearch}>
                  clear the search
                </Typography>
              </Typography>
            </MuiBox>
          ) : null}
        </>
      ) : null}
    </>
  );
};
